import { useBrandRequestContext } from '@/layouts/Brand/BrandRequestContext';
import {
  isArchived,
  isArchivedAndExportedToZendesk,
  isInAnalysisStep,
  isInDraftStep,
  isInExchangeStep,
  isInFirstServiceChoiceStep,
  isInInitialTransitStep,
  isInLaterServiceChoiceStep,
  isInLegacyPaymentStepWithValidation,
  isInPaymentStep,
  isInProcessingStep,
  isInQualityControlStep,
  isInRefundStep,
  isInRepairStep,
  isInTransitStep,
  isInValidationStep,
} from '@/models/request';
import { ArchivedNone } from '@/routes/Brand/Requests/Request/components/shared/ArchivedNone';
import UnhandledStep from '@/routes/Brand/Requests/Request/components/shared/UnhandledStep';

import Analysis from './components/Analysis';
import Archived from './components/Archived';
import Completed from './components/Completed';
import Delivery from './components/Delivery';
import Estimation from './components/Estimation';
import Exchange from './components/Exchange';
import Payment from './components/Payment';
import PaymentLegacy from './components/PaymentLegacy';
import QualityControl from './components/QualityControl';
import Refund from './components/Refund';
import Repair from './components/Repair';
import Transit from './components/Transit';
import Validation from './components/Validation';

const ClientRequestView = () => {
  const { request } = useBrandRequestContext();

  if (isArchived(request) && !isArchivedAndExportedToZendesk(request)) {
    const shouldRequestFeedback = request.articles.some(
      (article) => !!article.repairedAt || !!article.exchangeConfirmedAt
    );
    const isServiceChoiceNone = request.allArticles.every(
      (article) => article.snapshot.serviceChoice === 'none'
    );

    if (shouldRequestFeedback) {
      return <Completed request={request} />;
    } else if (isServiceChoiceNone) {
      return <ArchivedNone request={request} />;
    } else {
      return <Archived />;
    }
  }

  if (
    isInDraftStep(request) ||
    isInFirstServiceChoiceStep(request) ||
    isArchivedAndExportedToZendesk(request)
  ) {
    return <Estimation request={request} />;
  }

  if (isInValidationStep(request)) {
    return <Validation request={request} />;
  }

  if (isInExchangeStep(request)) {
    return <Exchange request={request} />;
  }

  if (isInQualityControlStep(request)) {
    return <QualityControl request={request} />;
  }

  if (isInInitialTransitStep(request)) {
    return <Transit request={request} />;
  }

  const isRequestInTransitToAnotherWorkshopAfterRequalificationRefused = request.articles.some(
    (article) =>
      !!article.requalificationRefusedAt &&
      !!article.workshopId &&
      article.toEntityId === article.workshopId
  );

  if (
    isInAnalysisStep(request) ||
    isInLaterServiceChoiceStep(request) ||
    isRequestInTransitToAnotherWorkshopAfterRequalificationRefused
  ) {
    return <Analysis request={request} />;
  }

  if (isInPaymentStep(request)) {
    if (isInLegacyPaymentStepWithValidation(request)) {
      return <PaymentLegacy request={request} />;
    } else {
      return <Payment request={request} />;
    }
  }

  if (isInRefundStep(request)) {
    return <Refund request={request} />;
  }

  if (isInRepairStep(request) || isInProcessingStep(request)) {
    return <Repair request={request} />;
  }

  if (isInTransitStep(request)) {
    return <Delivery request={request} />;
  }

  // If we reach this point, it means that the request is in an unknown step
  // We are still showing something as not to scare the user
  // But we are also sending an error to Sentry
  return <UnhandledStep request={request} />;
};

export default ClientRequestView;
